import {
  ArrowTopRightOnSquareIcon,
  Cog8ToothIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import {
  extractWizProperty,
  formatWizTimestamp,
  getWizSeverityClass,
} from '@/sections/poe/processors/wiz/helpers';
import type {
  WizParsedData,
  WizRenderContext,
} from '@/sections/poe/processors/wiz/types';
import { isValidExternalURL } from '@/utils/url.util';

export function renderConfiguration(
  parsed: WizParsedData,
  ctx: WizRenderContext
): void {
  const { wizData } = parsed;
  const { sections, tempTabs, mainTabKeys } = ctx;

  if (
    wizData &&
    wizData['configuration finding'] &&
    mainTabKeys.includes('DETECTION')
  ) {
    const configFinding = wizData['configuration finding'];
    sections['wiz_configuration'] = (
      <div className="space-y-4">
        <div className="border-warning border-l-4 p-4">
          <div className="flex items-center">
            <Cog8ToothIcon className="text-warning mr-2 size-5" />
            <h4 className="text-chariot-text-primary font-semibold">
              Configuration Issue
            </h4>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="border-chariot-stroke rounded-lg border p-4">
            <h5 className="text-chariot-text-primary mb-3 font-medium">
              Rule Details
            </h5>
            <div className="space-y-2 text-sm">
              {extractWizProperty(configFinding, 'configurationRuleName') && (
                <div>
                  <strong>Rule:</strong>{' '}
                  {extractWizProperty(configFinding, 'configurationRuleName')}
                </div>
              )}
              {extractWizProperty(
                configFinding,
                'configurationRuleShortName'
              ) && (
                <div>
                  <strong>Rule ID:</strong>{' '}
                  <code>
                    {extractWizProperty(
                      configFinding,
                      'configurationRuleShortName'
                    )}
                  </code>
                </div>
              )}
              {extractWizProperty(configFinding, 'severity') && (
                <div>
                  <strong>Severity:</strong>{' '}
                  <span
                    className={getWizSeverityClass(
                      extractWizProperty(configFinding, 'severity')
                    )}
                  >
                    {extractWizProperty(configFinding, 'severity')}
                  </span>
                </div>
              )}
              {extractWizProperty(configFinding, 'status') && (
                <div>
                  <strong>Status:</strong>{' '}
                  <span className="text-error">
                    {extractWizProperty(configFinding, 'status')}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="border-chariot-stroke rounded-lg border p-4">
            <h5 className="text-chariot-text-primary mb-3 font-medium">
              Configuration
            </h5>
            <div className="space-y-2 text-sm">
              {extractWizProperty(configFinding, 'currentConfig') && (
                <div>
                  <strong>Current:</strong>{' '}
                  <code className="bg-error-selected text-error rounded px-2 py-1 text-xs">
                    {extractWizProperty(configFinding, 'currentConfig')}
                  </code>
                </div>
              )}
              {extractWizProperty(configFinding, 'expectedConfig') && (
                <div>
                  <strong>Expected:</strong>{' '}
                  <span className="text-success">
                    {extractWizProperty(configFinding, 'expectedConfig')}
                  </span>
                </div>
              )}
              {extractWizProperty(configFinding, 'cloudPlatform') && (
                <div>
                  <strong>Platform:</strong>{' '}
                  {extractWizProperty(configFinding, 'cloudPlatform')}
                </div>
              )}
              {extractWizProperty(configFinding, 'updatedAt') && (
                <div>
                  <strong>Updated:</strong>{' '}
                  {formatWizTimestamp(
                    extractWizProperty(configFinding, 'updatedAt')
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {extractWizProperty(configFinding, 'configLink') &&
          isValidExternalURL(
            extractWizProperty(configFinding, 'configLink') || ''
          ) && (
            <div className="border-chariot-stroke rounded-lg border p-3">
              <a
                href={extractWizProperty(configFinding, 'configLink') || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-chariot-accent hover:text-chariot-accent-hover flex items-center text-sm"
              >
                <ArrowTopRightOnSquareIcon className="mr-1 size-4" />
                View Configuration Documentation
              </a>
            </div>
          )}
      </div>
    );
    tempTabs['DETECTION'].push({
      label: 'Configuration',
      value: 'wiz_configuration',
    });
  }
}
